import type { TabManager, TabFileState } from './TabManager.js';

export class TabBar {
  private static readonly FILE_CHOOSER_ICON = `
    <svg class="open-tab-type-icon" xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor" aria-hidden="true">
      <path d="M120-120v-720h720v720H120Zm80-80h120v-560H200v560Zm200 0h360v-560H400v560Z"/>
    </svg>
  `;

  private static readonly PAGE_ICON = `
    <svg class="open-tab-type-icon page" xmlns="http://www.w3.org/2000/svg" height="12" viewBox="0 -960 960 960" width="12" fill="currentColor" aria-hidden="true">
      <path d="M320-240h320v-80H320v80Zm0-160h320v-80H320v80ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Z"/>
    </svg>
  `;

  private static readonly COMPONENT_ICON = `
    <svg class="open-tab-type-icon component" xmlns="http://www.w3.org/2000/svg" height="12" viewBox="0 -960 960 960" width="12" fill="currentColor" aria-hidden="true">
      <path d="M352-120H200q-33 0-56.5-23.5T120-200v-152q48 0 84-30.5t36-77.5q0-47-36-77.5T120-568v-152q0-33 23.5-56.5T200-800h160q0-42 29-71t71-29q42 0 71 29t29 71h160q33 0 56.5 23.5T800-720v160q42 0 71 29t29 71q0 42-29 71t-71 29v160q0 33-23.5 56.5T720-120H568q0-50-31.5-85T460-240q-45 0-76.5 35T352-120Z"/>
    </svg>
  `;

  private container: HTMLElement;
  private manager: TabManager;
  private contextMenu: HTMLElement | null = null;
  /** Path of the tab currently being dragged, or null */
  private dragPath: string | null = null;
  private boundHideContextMenu: (e: MouseEvent) => void;
  private boundKeyHandler: (e: KeyboardEvent) => void;

  /** Called when user clicks a tab to switch to it */
  onTabClick: ((path: string) => Promise<void>) | null = null;
  /** Called when user closes a tab */
  onTabClose: ((path: string) => Promise<void>) | null = null;
  /** Called when user chooses "Close Others" */
  onTabCloseAllExcept: ((path: string) => Promise<void>) | null = null;
  /** Called when user wants to open the file chooser */
  onOpenFileChooser: (() => void) | null = null;
  /** Optional override for a tab's display name. Return null to fall back to the default. */
  getDisplayNameOverride: ((tab: TabFileState) => string | null) | null = null;
  /** Called when the user clicks the tab that is already active (windowed mode restores a minimized window). */
  onActiveTabClick: ((path: string) => void) | null = null;
  /** Windowed mode: report whether a tab's window is minimized so the bar can style it as such. */
  isTabMinimized: ((path: string) => boolean) | null = null;
  /** Called after every render (lets windowed mode refresh its window chrome in lockstep). */
  onRender: (() => void) | null = null;
  /** Extra controls rendered at the right end of the bar (windowed mode's Tile / Cascade / Exit). */
  extraActions: HTMLElement | null = null;

  constructor(container: HTMLElement, manager: TabManager) {
    this.container = container;
    this.manager = manager;

    // Dismiss on mousedown outside the menu (not click, so selection drags
    // released outside don't dismiss it). Presses inside the menu are ignored
    // here — menu items close it themselves after their click handler runs.
    this.boundHideContextMenu = (e: MouseEvent) => {
      if (this.contextMenu && this.contextMenu.contains(e.target as Node)) return;
      this.hideContextMenu();
    };
    this.boundKeyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.hideContextMenu();
    };

    // Middle-click on the tab bar to close tabs (skips pinned)
    this.container.addEventListener('auxclick', (e: MouseEvent) => {
      if (e.button === 1) {
        const tabEl = (e.target as HTMLElement).closest('.open-tab') as HTMLElement | null;
        if (tabEl?.dataset.path && !this.manager.isPinned(tabEl.dataset.path)) {
          e.preventDefault();
          this.onTabClose?.(tabEl.dataset.path);
        }
      }
    });
  }

  /** Render or re-render the tab bar from manager state */
  render(): void {
    this.container.innerHTML = '';
    this.container.appendChild(this.createFileChooserTab());
    const tabs = this.manager.tabs;
    const activeTab = this.manager.activeTab;

    // Defense-in-depth: skip duplicate paths even if they somehow slip through
    const rendered = new Set<string>();
    for (const tab of tabs) {
      if (rendered.has(tab.path)) continue;
      rendered.add(tab.path);
      const el = this.createTabElement(tab, tab.path === activeTab);
      this.container.appendChild(el);
    }
    this.container.appendChild(this.createSpacerElement());
    if (this.extraActions) this.container.appendChild(this.extraActions);

    this.updateVisibility();
    this.onRender?.();
  }

  /** Keep tab bar visible */
  updateVisibility(): void {
    this.container.classList.remove('hidden');
  }

  private createFileChooserTab(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'open-tab file-chooser-tab';
    el.title = 'Open file chooser (Alt+Space)';

    const iconEl = document.createElement('span');
    iconEl.className = 'open-tab-icon';
    iconEl.innerHTML = TabBar.FILE_CHOOSER_ICON;
    el.appendChild(iconEl);

    el.addEventListener('click', () => {
      this.onOpenFileChooser?.();
    });

    return el;
  }

  private createSpacerElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'tab-bar-spacer';
    el.setAttribute('aria-hidden', 'true');

    // Dropping on the empty area after the tabs moves the dragged tab to the end
    el.addEventListener('dragover', (e: DragEvent) => {
      if (!this.dragPath) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      this.clearDropIndicators();
      const tabs = this.container.querySelectorAll('.open-tab[data-path]');
      tabs[tabs.length - 1]?.classList.add('drop-after');
    });
    el.addEventListener('dragleave', () => this.clearDropIndicators());
    el.addEventListener('drop', (e: DragEvent) => {
      if (!this.dragPath) return;
      e.preventDefault();
      const path = this.dragPath;
      this.endDrag();
      this.manager.moveTab(path, null);
    });

    return el;
  }

  /** Remove all drop position indicators */
  private clearDropIndicators(): void {
    this.container.querySelectorAll('.drop-before, .drop-after').forEach(el => {
      el.classList.remove('drop-before', 'drop-after');
    });
  }

  /** Reset drag state and visuals */
  private endDrag(): void {
    this.dragPath = null;
    this.clearDropIndicators();
    this.container.querySelectorAll('.open-tab.dragging').forEach(el => {
      el.classList.remove('dragging');
    });
  }

  /** Attach drag-and-drop reordering behavior to a tab element */
  private attachDragHandlers(el: HTMLElement, path: string): void {
    el.draggable = true;

    el.addEventListener('dragstart', (e: DragEvent) => {
      this.dragPath = path;
      el.classList.add('dragging');
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', path);
      }
    });

    el.addEventListener('dragend', () => this.endDrag());

    el.addEventListener('dragover', (e: DragEvent) => {
      if (!this.dragPath || this.dragPath === path) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      const rect = el.getBoundingClientRect();
      const before = e.clientX < rect.left + rect.width / 2;
      this.clearDropIndicators();
      el.classList.add(before ? 'drop-before' : 'drop-after');
    });

    el.addEventListener('dragleave', (e: DragEvent) => {
      // Only clear if actually leaving this tab (not entering a child)
      if (!el.contains(e.relatedTarget as Node)) {
        el.classList.remove('drop-before', 'drop-after');
      }
    });

    el.addEventListener('drop', (e: DragEvent) => {
      if (!this.dragPath || this.dragPath === path) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const before = e.clientX < rect.left + rect.width / 2;
      const dragged = this.dragPath;
      this.endDrag();
      this.manager.moveTab(dragged, path, before);
    });
  }

  /** Create a single tab DOM element */
  private createTabElement(tab: TabFileState, isActive: boolean): HTMLElement {
    const el = document.createElement('div');
    el.className = 'open-tab';
    if (isActive) el.classList.add('active');
    if (tab.hasUnsavedChanges) el.classList.add('modified');
    const isPinned = this.manager.isPinned(tab.path);
    if (isPinned) el.classList.add('pinned');
    if (this.isTabMinimized?.(tab.path)) el.classList.add('minimized');
    el.dataset.path = tab.path;

    const iconEl = document.createElement('span');
    iconEl.className = 'open-tab-icon';
    iconEl.innerHTML = tab.isComponent ? TabBar.COMPONENT_ICON : TabBar.PAGE_ICON;
    iconEl.title = tab.isComponent ? 'Component' : 'Page';
    el.appendChild(iconEl);

    // File name
    const nameEl = document.createElement('span');
    nameEl.className = 'open-tab-name';
    nameEl.textContent = this.getDisplayName(tab);
    nameEl.title = tab.path;
    el.appendChild(nameEl);

    // Modified dot
    const dotEl = document.createElement('span');
    dotEl.className = 'open-tab-modified';
    el.appendChild(dotEl);

    // Close button — omitted for pinned tabs
    if (!isPinned) {
      const closeEl = document.createElement('span');
      closeEl.className = 'open-tab-close';
      closeEl.innerHTML = '&times;';
      closeEl.title = 'Close';
      closeEl.addEventListener('click', (e) => {
        e.stopPropagation();
        this.onTabClose?.(tab.path);
      });
      el.appendChild(closeEl);
    }

    // Click to switch
    el.addEventListener('click', () => {
      if (tab.path !== this.manager.activeTab) {
        this.onTabClick?.(tab.path);
      } else {
        this.onActiveTabClick?.(tab.path);
      }
    });

    // Right-click context menu (none for pinned tabs — they can't be closed)
    if (!isPinned) {
      el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        this.showContextMenu(e, tab.path);
      });
    }

    // Drag-and-drop reordering
    this.attachDragHandlers(el, tab.path);

    return el;
  }

  /** Display name for an open tab path (same rules as the tab labels). */
  displayNameForPath(path: string): string {
    const tab = this.manager.getTab(path);
    if (!tab) return path;
    return this.getDisplayName(tab);
  }

  /** Get a short display name for a tab */
  private getDisplayName(tab: TabFileState): string {
    const override = this.getDisplayNameOverride?.(tab);
    if (override) return override;
    if (tab.isComponent) {
      const name = tab.path.replace('_components/', '');
      return name.replace('.md', '');
    }
    const parts = tab.path.split('/');
    const filename = parts[parts.length - 1].replace('.src.md', '');
    // If there are sibling tabs with the same filename, show dir/filename
    const hasDuplicate = this.manager.tabs.some(
      t => t.path !== tab.path &&
        t.path.split('/').pop()!.replace('.src.md', '').replace('.md', '') === filename
    );
    if (hasDuplicate && parts.length > 1) {
      return `${parts[parts.length - 2]}/${filename}`;
    }
    return filename;
  }

  /** Show context menu on right-click */
  private showContextMenu(e: MouseEvent, path: string): void {
    this.hideContextMenu();

    const menu = document.createElement('div');
    menu.className = 'tab-context-menu';
    menu.style.left = `${e.clientX}px`;
    menu.style.top = `${e.clientY}px`;

    // Close
    const closeItem = document.createElement('button');
    closeItem.className = 'tab-context-item';
    closeItem.textContent = 'Close';
    closeItem.addEventListener('click', () => {
      this.hideContextMenu();
      this.onTabClose?.(path);
    });
    menu.appendChild(closeItem);

    // Close Others
    if (this.manager.tabCount > 1) {
      const closeOthersItem = document.createElement('button');
      closeOthersItem.className = 'tab-context-item';
      closeOthersItem.textContent = 'Close Others';
      closeOthersItem.addEventListener('click', () => {
        this.hideContextMenu();
        this.onTabCloseAllExcept?.(path);
      });
      menu.appendChild(closeOthersItem);
    }

    document.body.appendChild(menu);
    this.contextMenu = menu;

    // Reposition if off-screen
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
      menu.style.left = `${window.innerWidth - rect.width - 4}px`;
    }
    if (rect.bottom > window.innerHeight) {
      menu.style.top = `${window.innerHeight - rect.height - 4}px`;
    }

    // Dismiss on outside press or Escape
    requestAnimationFrame(() => {
      document.addEventListener('mousedown', this.boundHideContextMenu);
      document.addEventListener('keydown', this.boundKeyHandler);
    });
  }

  /** Hide context menu */
  private hideContextMenu(): void {
    if (this.contextMenu) {
      this.contextMenu.remove();
      this.contextMenu = null;
    }
    document.removeEventListener('mousedown', this.boundHideContextMenu);
    document.removeEventListener('keydown', this.boundKeyHandler);
  }

  /** Clean up */
  destroy(): void {
    this.hideContextMenu();
    this.container.innerHTML = '';
  }
}
